import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { setPref, typeFromCategory } from '../lib/pref';
import { toast } from 'sonner';
import api, { revealsApi } from '../services/api';
import { useAuth } from './AuthContext';
import { useLocationPref } from './LocationContext';
import { track } from '../utils/analytics';
import { lazyWhenOpen } from '../components/lazyWhenOpen';
import { artistName } from '../lib/artist';

// R6 — the reveal sheets' code loads on first open.
const OtpSheet = lazyWhenOpen(() => import('../components/OtpSheet'));
const RevealDetailsSheet = lazyWhenOpen(() => import('../components/RevealDetailsSheet'));
const RevealLimitSheet = lazyWhenOpen(() => import('../components/RevealLimitSheet'));

const Ctx = createContext(null);

const OCC_KEY = 'as_reveal_occasion';
const EVENT_DATE_KEY = 'as_event_date';

const idOf = (a) => a?.artist_id || a?.id || null;
const catOf = (a) => a?.category || a?.artist_type || a?.primary_category || null;
const cityOf = (a) => a?.city || a?.base_city || null;
const ls = (k) => { try { return localStorage.getItem(k) || ''; } catch (_) { return ''; } };
const lsSet = (k, v) => { try { if (v) localStorage.setItem(k, v); } catch (_) { /* ignore */ } };

// The occasion in the current URL (e.g. /delhi-ncr/kitty-party), if it is a live one.
const occFromPath = (list) => {
  const parts = window.location.pathname.split('/');
  return (list.find((o) => parts.includes(o.slug)) || {}).slug || '';
};

export const ContactRevealProvider = ({ children }) => {
  const { user } = useAuth();
  const loc = useLocationPref();
  const [revealed, setRevealed] = useState({}); // artist_id -> { number, call_href, whatsapp_href }
  const [otpOpen, setOtpOpen] = useState(false);
  const [rateOpen, setRateOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [occasions, setOccasions] = useState([]);
  const [occasion, setOccasion] = useState('');
  const [date, setDate] = useState('');
  const [pendingArtist, setPendingArtist] = useState(null);
  const ctxRef = useRef({ occasion: '', date: '' });

  const isRevealed = useCallback((id) => !!revealed[id], [revealed]);

  const loadOccasions = useCallback(async () => {
    if (occasions.length) return occasions;
    try {
      const r = await api.get('/occasions/home-tiles');
      const list = r.data.all || [];
      setOccasions(list);
      return list;
    } catch (_) { return []; }
  }, [occasions]);

  const doReveal = useCallback(async (artist) => {
    const id = idOf(artist);
    if (!id) return;
    const { occasion: occ, date: event_date } = ctxRef.current;
    try {
      const res = await revealsApi.reveal({
        artist_id: id,
        source_page: window.location.pathname + window.location.search,
        occasion: occ,
        ...(event_date ? { event_date } : {}),
        ...(loc.area ? { area: loc.area } : {}),
      });
      const d = res.data || {};
      setRevealed((m) => ({
        ...m,
        [id]: { number: d.number, call_href: d.call_href, whatsapp_href: d.whatsapp_href },
      }));
      if (!d.dedup) toast.success('Saved to your shortlist');
      setPref({ category: typeFromCategory(catOf(artist)), occasion: occ });
      track('contact_reveal', {
        artist_id: id,
        category: catOf(artist),
        city: cityOf(artist),
        occasion: occ || null,
        source_page: window.location.pathname,
      });
    } catch (e) {
      const status = e?.response?.status;
      if (status === 429) setRateOpen(true);
      else if (status === 409) toast.error("This artist hasn't shared a number yet.");
      else toast.error('Could not show the number. Please try again.');
    }
  }, [loc.area]);

  const requestReveal = useCallback(async (artist) => {
    const id = idOf(artist);
    if (!id || revealed[id]) return;
    track('contact_reveal_start', {
      artist_id: id,
      category: catOf(artist),
      city: cityOf(artist),
      source_page: window.location.pathname,
    });
    setPendingArtist(artist);
    const list = await loadOccasions();
    const remembered = ls(OCC_KEY);
    setOccasion(occFromPath(list) || (list.some((o) => o.slug === remembered) ? remembered : ''));
    setDate(ls(EVENT_DATE_KEY));
    setDetailsOpen(true);
  }, [revealed, loadOccasions]);

  const continueDetails = useCallback(() => {
    if (!occasion) return;
    ctxRef.current = { occasion, date };
    lsSet(OCC_KEY, occasion);
    lsSet(EVENT_DATE_KEY, date);
    setDetailsOpen(false);
    if (user?.role === 'customer') {
      doReveal(pendingArtist);
      setPendingArtist(null);
    } else {
      setOtpOpen(true);
    }
  }, [occasion, date, user, pendingArtist, doReveal]);

  const handleVerified = useCallback(() => {
    setOtpOpen(false);
    const a = pendingArtist;
    setPendingArtist(null);
    if (a) doReveal(a);
  }, [pendingArtist, doReveal]);

  return (
    <Ctx.Provider value={{ revealed, isRevealed, requestReveal }}>
      {children}

      <RevealDetailsSheet
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        artistName={pendingArtist ? artistName(pendingArtist) : null}
        occasions={occasions}
        occasion={occasion}
        setOccasion={setOccasion}
        date={date}
        setDate={setDate}
        onContinue={continueDetails}
      />

      <OtpSheet
        open={otpOpen}
        onOpenChange={setOtpOpen}
        context="artist"
        artistName={pendingArtist ? artistName(pendingArtist) : null}
        onVerified={handleVerified}
      />

      <RevealLimitSheet open={rateOpen} onOpenChange={setRateOpen} />
    </Ctx.Provider>
  );
};

export const useContactReveal = () => {
  const ctx = useContext(Ctx);
  if (!ctx) return { revealed: {}, isRevealed: () => false, requestReveal: () => {} };
  return ctx;
};

export default ContactRevealProvider;
